import {
  Buildings,
  UsersThree,
  Baby,
  ChalkboardTeacher,
  Sparkle,
  ShieldCheck,
  ArrowRight,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { getInquiryStats } from "@/lib/inquiries";
import { getPhotoModerationQueueCount } from "@/lib/photoModeration";
import TeacherInstallNudgeCard from "@/components/admin/TeacherInstallNudgeCard";
import AdminSystemWidget from "@/components/admin/AdminSystemWidget";
import AdminLaunchReadinessWidget from "@/components/admin/AdminLaunchReadinessWidget";
import DirectorMorningBrief from "@/components/admin/DirectorMorningBrief";

const INITIAL_STATS = [
  { key: "organizations", label: "Organizations", icon: Buildings },
  { key: "families", label: "Families", icon: UsersThree },
  { key: "children", label: "Children", icon: Baby },
  { key: "classrooms", label: "Classrooms", icon: ChalkboardTeacher },
];

export default function AdminDashboard() {
  const [counts, setCounts] = useState({
    organizations: null,
    families: null,
    children: null,
    classrooms: null,
  });
  const [inquiryStats, setInquiryStats] = useState(null);
  const [moderationCount, setModerationCount] = useState(null);

  // Load inquiry KPIs (fire-and-log; falls back silently if RLS/non-admin).
  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let alive = true;
    (async () => {
      try {
        const s = await getInquiryStats();
        if (alive) setInquiryStats(s);
      } catch {
        /* no-op */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Photo moderation queue depth (flagged count). Same non-blocking pattern.
  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let alive = true;
    (async () => {
      try {
        const n = await getPhotoModerationQueueCount();
        if (alive) setModerationCount(n);
      } catch {
        /* no-op */
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      // Demo numbers so the dashboard still feels alive during preview.
      setCounts({
        organizations: 1,
        families: 24,
        children: 38,
        classrooms: 4,
      });
      return;
    }
    let cancelled = false;
    (async () => {
      const tables = ["organizations", "families", "children", "classrooms"];
      const results = await Promise.all(
        tables.map((t) =>
          // Use GET (not HEAD) because some preview/proxy layers abort HEAD
          // before PostgREST can respond. `limit(1)` keeps payload tiny while
          // the Content-Range header carries the exact count.
          supabase.from(t).select("id", { count: "exact" }).limit(1),
        ),
      );
      if (cancelled) return;
      const next = { ...counts };
      tables.forEach((t, i) => {
        next[t] = results[i].error ? "—" : results[i].count ?? 0;
      });
      setCounts(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <section>
        <p className="overline text-primary-hover">Admin overview</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
          Organization at a glance
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Operational snapshot pulled from Supabase. Use the navigation to manage
          families, children, classrooms, and staff.
        </p>
      </section>

      <DirectorMorningBrief />

      <section
        data-testid="admin-stats-grid"
        className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-200 shadow-card xl:grid-cols-4"
      >
        {INITIAL_STATS.map(({ key, label, icon: Icon }) => (
          <article
            key={key}
            data-testid={`admin-stat-${key}`}
            className="flex items-center gap-4 bg-white p-5"
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
              <Icon size={22} weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="overline text-neutral-500">{label}</p>
              <p className="tnum mt-1.5 font-heading text-3xl font-bold leading-none tracking-tight text-neutral-900">
                {counts[key] ?? "…"}
              </p>
            </div>
          </article>
        ))}
      </section>

      <AdminSystemWidget />
      <AdminLaunchReadinessWidget />

      <TeacherInstallNudgeCard />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Link
          to="/admin/inquiries"
          data-testid="admin-dashboard-inquiries-card"
          className="group flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-card transition-all hover:border-primary/40 hover:shadow-raised"
        >
          <div>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
              <Sparkle size={22} weight="duotone" />
            </div>
            <p className="overline mt-5 text-neutral-500">
              New inquiries this week
            </p>
            <p className="tnum mt-2 font-heading text-4xl font-bold tracking-tight text-neutral-900">
              {inquiryStats?.this_week ?? "…"}
            </p>
            <p className="mt-1.5 text-xs text-neutral-500">
              {inquiryStats?.new_count != null
                ? `${inquiryStats.new_count} awaiting follow-up · ${inquiryStats?.total ?? 0} total`
                : "Lead capture from your public site."}
            </p>
          </div>
          <ArrowRight
            size={18}
            weight="bold"
            className="mt-1 text-neutral-300 transition group-hover:translate-x-1 group-hover:text-primary-hover"
          />
        </Link>

        <Link
          to="/admin/photos/moderation"
          data-testid="admin-dashboard-photo-moderation-card"
          data-has-queue={moderationCount ? "true" : "false"}
          className={`group flex items-start justify-between gap-4 rounded-2xl border p-6 shadow-card transition-all hover:shadow-raised ${
            moderationCount
              ? "border-primary/40 bg-primary/[0.04] hover:border-primary/60"
              : "border-neutral-200 bg-white hover:border-primary/40"
          }`}
        >
          <div>
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                moderationCount ? "bg-primary/15 text-primary-hover" : "bg-neutral-100 text-neutral-600"
              }`}
            >
              <ShieldCheck size={22} weight="duotone" />
            </div>
            <p className="overline mt-5 text-neutral-500">
              Photos to review
            </p>
            <p
              data-testid="admin-dashboard-photo-moderation-count"
              className="tnum mt-2 font-heading text-4xl font-bold tracking-tight text-neutral-900"
            >
              {moderationCount ?? "…"}
            </p>
            <p className="mt-1.5 text-xs text-neutral-500">
              {moderationCount
                ? "Pending teacher uploads or parent reports awaiting your review."
                : "Inbox is clear — nothing waiting for your review."}
            </p>
          </div>
          <ArrowRight
            size={18}
            weight="bold"
            className="mt-1 text-neutral-300 transition group-hover:translate-x-1 group-hover:text-primary-hover"
          />
        </Link>
      </section>
    </div>
  );
}
